import {
  PwsDynamicReportExecute,
  OrderTypeCountryGet,
  OrderTypePoePriceCatGet
} from '../api/Common.ts'
import type {
  IPwsDynamicReportExecuteRequest,
  IOrderTypeCountryRequest,
  IOrderTypeCountryResponse,
  IOrderTypePoePriceCatRequest,
  IOrderTypePoePriceCatResponse,
  IOrderTypePoePriceCatItem,
} from '../types/CommonTypes'
import { useStoreLogger } from '../logger.ts'
import { GetConsultantFromLocalStorageOrApi } from './ConsultantService'
import { GetBeeNumberAuthenicatedUser } from '../stores/SettingsStore.ts'

export async function DynamicReportExecute(reportRequest: IPwsDynamicReportExecuteRequest): Promise<any | null> {
  const reportResponse = await PwsDynamicReportExecute(reportRequest)
  return reportResponse
}

export const GenOrderTypeCountryLocalStorageKey = 'GOTC_'

export async function GetOrderTypeCountryFromLocalStorageOrApi(
  request: IOrderTypeCountryRequest,
): Promise<IOrderTypeCountryResponse | null> {
  const logger = useStoreLogger()
  logger.debug('GetOrderTypeCountryFromLocalStorageOrApi', request.orderTypeId, request.countryCode)

  let gotc = GetOrderTypeCountryFromLocalStorage(request)
  if (gotc) return gotc

  try {
    const gotcResponse = await OrderTypeCountryGet(request)
    logger.debug('GetCategoriesFromLocalStorageOrApi', gotcResponse)
    if (gotcResponse != null) {
      SaveOrderTypeCountryToLocalStorage(request.orderTypeId, request.countryCode, gotcResponse)
      gotc = GetOrderTypeCountryFromLocalStorage(request)
    }
  } catch (error) {
    logger.debug(error)
    const noOverride: IOrderTypeCountryResponse = { item: { orderTypeId: request.orderTypeId, countryCode: request.countryCode, orderTypeIdOverride: request.orderTypeId } }
    SaveOrderTypeCountryToLocalStorage(request.orderTypeId, request.countryCode, noOverride)
    gotc = GetOrderTypeCountryFromLocalStorage(request)
  }

  return gotc
}


export function GetOrderTypeCountryFromLocalStorage(
  otCountryRequest: IOrderTypeCountryRequest
): IOrderTypeCountryResponse | null {
  const logger = useStoreLogger()
  logger.debug('GetOrderTypeCountryFromLocalStorage', otCountryRequest.orderTypeId)

  const json = localStorage.getItem(GenOrderTypeCountryLocalStorageKey + otCountryRequest.orderTypeId + '_' + otCountryRequest.countryCode)
  if (json) {
    logger.debug('Categories found in local storage')
    const gotc: IOrderTypeCountryResponse = JSON.parse(json)
    if (gotc) {
      return gotc
    }
  }
  logger.debug('Order Type Country not found in local storage')
  return null
}

export function SaveOrderTypeCountryToLocalStorage(orderTypeId: string, countryCode: string, gotcResponse: IOrderTypeCountryResponse) {
  const newValue = JSON.stringify(gotcResponse)
  const logger = useStoreLogger()
  logger.debug('SaveOrderTypeCountryToLocalStorage', newValue)
  const key = GenOrderTypeCountryLocalStorageKey + gotcResponse.item.orderTypeId + '_' + gotcResponse.item.countryCode
  localStorage.setItem(key, newValue)
  window.dispatchEvent(
    new StorageEvent('storage', { key, newValue, storageArea: localStorage }),
  )
}

export async function GetOrderTypeCountry(origOrderTypeId : string) : Promise<string> {
  // Get authenticated user's bee number and load consultant data
  const beeNumber = await GetBeeNumberAuthenicatedUser()
  if (beeNumber) {
    const consultant = await GetConsultantFromLocalStorageOrApi(beeNumber)

    if (consultant && origOrderTypeId) {
      // Call GetOrderTypeCountry with consultant countryCode and ordertypeid
      const orderTypeCountryResponse = await GetOrderTypeCountryFromLocalStorageOrApi({
        orderTypeId: origOrderTypeId,
        countryCode: consultant.countryCode
      })

      // Update orderTypeId if there's an override
      if (orderTypeCountryResponse?.item?.orderTypeIdOverride) {
        return orderTypeCountryResponse.item.orderTypeIdOverride
      }
    }
  }

  return origOrderTypeId
}

export const GenOrderTypePoePriceCatLocalStorageKey = 'GOTPOEPC_'

export async function GetOrderTypePoePriceCatFromLocalStorageOrApi(
  request: IOrderTypePoePriceCatRequest,
): Promise<IOrderTypePoePriceCatItem[]> {
  const logger = useStoreLogger()
  logger.debug('GetOrderTypePoePriceCatFromLocalStorageOrApi', request.orderTypeId)

  let gotc = GetOrderTypePoePriceCatFromLocalStorage(request)
  if (gotc) return gotc

  const gotcResponse = await OrderTypePoePriceCatGet(request)
  logger.debug('GetCategoriesFromLocalStorageOrApi', gotcResponse)
  if (gotcResponse != null)
  {
    SaveOrderTypePoePriceCatToLocalStorage(request.orderTypeId, gotcResponse)
    gotc = GetOrderTypePoePriceCatFromLocalStorage(request)
  }

  if (gotc != null)
  {
    return gotc
  }
  else
  {
    const emptyArray : IOrderTypePoePriceCatItem[] = [{
      name: '',
      isCommissionable: false,
      isQualifying: false,
      applyDiscount: false,
      categoryCodes: [],
      buyingDiscountPercentOverride: null
    }]
    return emptyArray
  }
}

export function GetOrderTypePoePriceCatFromLocalStorage(
  otPoePriceCatRequest: IOrderTypePoePriceCatRequest
): IOrderTypePoePriceCatItem[] | null {
  const logger = useStoreLogger()
  logger.debug('GetOrderTypePoePriceCatFromLocalStorage', otPoePriceCatRequest.orderTypeId)

  const json = localStorage.getItem(GenOrderTypePoePriceCatLocalStorageKey + otPoePriceCatRequest.orderTypeId)
  if (json) {
    logger.debug('Categories found in local storage')
    const gotc: IOrderTypePoePriceCatItem[] = JSON.parse(json)
    if (gotc) {
      return gotc
    }
  }
  logger.debug('Order Type Poe Price Cat not found in local storage')
  return null
}

export function SaveOrderTypePoePriceCatToLocalStorage(orderTypeId: string, gotcResponse: IOrderTypePoePriceCatResponse) {
  const newValue = JSON.stringify(gotcResponse.items)
  const logger = useStoreLogger()
  logger.debug('SaveOrderTypePoePriceCatToLocalStorage', newValue)
  const key = GenOrderTypePoePriceCatLocalStorageKey + orderTypeId
  localStorage.setItem(key, newValue)
  window.dispatchEvent(
    new StorageEvent('storage', { key, newValue, storageArea: localStorage }),
  )
}

